import React, { useMemo } from 'react';
import { Badge, Button, Dropdown, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { SlashCircle, Envelope, Trash, FileEarmark } from 'react-bootstrap-icons';
import { canViewCollectedFees, isAdmin, isClient, isStaff } from '../../../../helpers/global';
import { computeCollectedFees } from '../../../../helpers/collectedFees';
import { getBgColorFromStatus } from '../../../../helpers/ui';
import useAppChoices from '../../../../hooks/useAppChoices';
import CircularProgressBar from '../../../circular-progress';
import SmallDropdownToggle from '../../../SmallDropdownToggle';
import HOAs from './HOAs';
import RequestHoaInfoTrigger from './RequestHoaInfoTrigger';
import {
  CLIENT_CANCELLED_INTERNAL_STATUSES,
  ORDER_TYPES,
  getClientFacingOrderStatus
} from '../../../../helpers/constants';
import { Calendar, Circle, DollarSign, Mail, Pencil, User } from 'lucide-react';
import RushIconSVG from '../../../icons/RushIconSVG';
import EtaClickableValue from '../EtaClickableValue';
import OrderDetailField from '../OrderDetailField';
import OrderPinToggle from '../OrderPinToggle';
import CancellationRequestedBadge from '../CancellationRequestedBadge';

const commonRows = [
  {
    label: 'Assigned Processor',
    key: 'assignedProcessors',
    isArray: true,
    objectKey: 'name',
    icon: <User className="icon-sm" />
  },
  { label: 'Hard Cost', key: 'hardCost', icon: <DollarSign className="icon-sm" /> },
  { label: 'Processing Fee', key: 'processingFee', icon: <DollarSign className="icon-sm" /> },
  { label: 'Collected Fees', key: 'collectedFees', icon: <DollarSign className="icon-sm" /> }
];

const commonRowsClient = [
  {
    label: 'Assigned Processor',
    key: 'assignedProcessors',
    isArray: true,
    objectKey: 'name',
    icon: <User className="icon-sm" />
  },
  {
    label: 'Processor Email',
    key: 'assignedProcessorEmails',
    isArray: true,
    objectKey: 'email',
    icon: <Mail className="icon-sm" />
  }
];

const priceFields = ['hardCost', 'totalCost', 'processingFee', 'collectedFees'];
const dateFields = ['dateDelivered', 'estimatedTurnAroundTime', 'neededDate', 'createdAt'];
const HARD_COST_REQUIRED_STATUSES = ['Information Requested', 'Ready for QA'];

const EstoppelDetails = ({
  estoppel: originalEstoppel,
  disabled,
  onStatusToggle,
  serviceCreation,
  onPaymentReminderClicked,
  hasPastDueUnpaidInvoices = false,
  service,
  onEditClick,
  onEditHoaSlotClick,
  onDeleteClick,
  onRequestCancellationClick,
  onUndoCancellationClick,
  onEtaUpdate,
  onSyncOrderHardcost,
  loadingMetaData,
  onOrderFieldUpdate,
  onTogglePin,
  isPinning = false
}) => {
  const role = useMemo(() => localStorage.getItem('user-role'), []);
  const currentUserInfo = useMemo(() => JSON.parse(localStorage.getItem('user') || '{}'), []);
  const canSeeCollectedFees = useMemo(() => canViewCollectedFees(currentUserInfo), [currentUserInfo]);
  const statuses = useAppChoices('Estoppel Status');
  const estoppel = useMemo(
    () => ({
      ...originalEstoppel,
      totalCost: ['hardCost', 'processingFee'].reduce((acc, field) => acc + (originalEstoppel[field] || 0), 0),
      collectedFees: computeCollectedFees(originalEstoppel, originalEstoppel.feeSourceInvoices),
      assignedProcessorEmails: originalEstoppel.assignedProcessors || []
    }),
    [originalEstoppel]
  );

  const showPaymentReminderButton = () =>
    isAdmin(role) && estoppel['status'] === 'Pending To Pay' && hasPastDueUnpaidInvoices;

  const fields = useMemo(() => {
    const baseRows = !isStaff(role)
      ? commonRowsClient
      : canSeeCollectedFees
      ? commonRows
      : commonRows.filter(r => r.key !== 'collectedFees');

    if (isClient(role) && estoppel.assignedProcessors?.length > 0) return baseRows;
    return baseRows.filter(r => r.key !== 'assignedProcessorEmails');
  }, [role, canSeeCollectedFees, estoppel.assignedProcessors]);

  return (
    <div className="card p-4 shadow-sm h-100 ">
      <div className="d-flex justify-content-between align-items-center flex-wrap">
        <div className="mb-2 mb-md-0">
          <div className="d-flex align-items-center">
            <OrderPinToggle
              order={originalEstoppel}
              disabled={disabled}
              isPinning={isPinning}
              onTogglePin={onTogglePin}
            />
            <h1 className="font-weight-semibold xlargeFont mb-0">Estoppel</h1>
            <div className="d-flex align-items-center ml-2" style={{ gap: '8px' }}>
              {estoppel?.rush && (
                <span
                  style={{
                    padding: '1px 6px',
                    fontSize: '12px',
                    fontWeight: '600'
                  }}
                  className="text-danger bg-danger-light rounded-pill"
                >
                  RUSH
                </span>
              )}
              {estoppel['cancellationRequested'] && (
                <CancellationRequestedBadge
                  disabled={disabled}
                  hideUndo={CLIENT_CANCELLED_INTERNAL_STATUSES.includes(estoppel['status'])}
                  onUndoClick={() => onUndoCancellationClick?.(estoppel)}
                />
              )}
            </div>
          </div>
          <div className="d-flex align-items-center text-muted midFont mt-1">Service No. {estoppel['accurateId']}</div>
        </div>
        <div>
          {!serviceCreation && showPaymentReminderButton() && (
            <Button
              variant="outline-dark"
              disabled={disabled}
              className="ml-1 d-inline-flex align-items-center shadow-sm"
              onClick={() => onPaymentReminderClicked(estoppel?._id)}
            >
              <Envelope size={13} className="mr-1" />
              Send Payment Reminder
            </Button>
          )}
          {!isClient(role) && (
            <>
              <Button
                variant="outline-success"
                disabled={disabled}
                className="ml-1 d-inline-flex align-items-center shadow-sm"
                onClick={() =>
                  window.open(
                    `/services/pipeline?serviceId=${service._id}&orderId=${estoppel._id}&reportType=${ORDER_TYPES.ESTOPPEL}`,
                    '_blank'
                  )
                }
              >
                <FileEarmark size={13} className="mr-1" />
                Open Report Builder
              </Button>
              <Button
                variant="outline-danger"
                className="ml-1 d-inline-flex align-items-center shadow-sm"
                onClick={() => onDeleteClick(estoppel)}
                disabled={disabled}
              >
                <Trash size={13} className="mr-1" />
                Delete
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Details Section */}
      <div>
        {(() => {
          const allFields = [
            {
              label: 'Status',
              key: 'status',
              icon: <Circle fill="currentColor" className="icon-sm" />,
              isSpecial: true,
              render: () =>
                isStaff(role) ? (
                  !serviceCreation && (
                    <Dropdown onClick={e => e.stopPropagation()} className="d-inline-block mr-1">
                      <OverlayTrigger
                        placement="bottom"
                        overlay={<Tooltip id={`tooltip-order-status-action`}>Change order status</Tooltip>}
                      >
                        <SmallDropdownToggle
                          variant=""
                          disabled={disabled}
                          style={{ border: 0, backgroundColor: getBgColorFromStatus(estoppel['status']).bg }}
                          size="sm"
                          aria-label="Change order status"
                        >
                          <span
                            className="d-inline-flex align-items-center largeFont font-weight-semibold p-1"
                            style={{ color: getBgColorFromStatus(estoppel['status']).text }}
                          >
                            {loadingMetaData?.status && <CircularProgressBar size={1} className="mr-2" />}
                            {estoppel['status']}
                          </span>
                        </SmallDropdownToggle>
                      </OverlayTrigger>

                      <Dropdown.Menu>
                        {statuses
                          ?.filter(s => s !== 'Pending')
                          .map(s => (
                            <Dropdown.Item
                              style={{
                                backgroundColor: getBgColorFromStatus(s).bg,
                                color: getBgColorFromStatus(s).text
                              }}
                              key={s}
                              onClick={() => onStatusToggle && onStatusToggle(s)}
                            >
                              {s}
                            </Dropdown.Item>
                          ))}
                      </Dropdown.Menu>
                    </Dropdown>
                  )
                ) : (
                  (() => {
                    const clientStatus = getClientFacingOrderStatus(
                      estoppel['status'],
                      estoppel['cancellationRequested']
                    );
                    const statusColors = getBgColorFromStatus(clientStatus);
                    return (
                      <Badge className="mr-1 p-2" style={{ backgroundColor: statusColors.bg }}>
                        <span className="midFont" style={{ color: statusColors.text }}>
                          {clientStatus === 'Completed/Unpaid' ? (
                            <>
                              Completed/<span className="text-danger">Unpaid</span>
                            </>
                          ) : (
                            clientStatus
                          )}
                        </span>
                      </Badge>
                    );
                  })()
                )
            },
            {
              label: 'ETA',
              key: 'estimatedTurnAroundTime',
              icon: <Calendar className="icon-sm" />,
              labelWarningTooltipText:
                estoppel?.status === 'Update' && !estoppel?.isEtaUpdatedAfterUpdateStatus
                  ? 'New ETA is required'
                  : undefined,
              isSpecial: true,
              render: () => (
                <EtaClickableValue
                  value={estoppel['estimatedTurnAroundTime']}
                  disabled={disabled}
                  loading={loadingMetaData?.eta}
                  onDateChange={onEtaUpdate}
                  needsNewEta={estoppel?.status === 'Update' && !estoppel?.isEtaUpdatedAfterUpdateStatus}
                />
              )
            },
            ...(!isClient(role)
              ? [
                  {
                    label: 'Rush',
                    key: 'rush',
                    icon: <RushIconSVG width="18px" height="18px" />
                  }
                ]
              : []),
            {
              label: 'Date Ordered',
              key: 'createdAt',
              icon: <Calendar className="icon-sm" />
            },
            ...(estoppel['dateDelivered']
              ? [
                  {
                    label: 'Date Delivered',
                    key: 'dateDelivered',
                    icon: <Calendar className="icon-sm" />
                  }
                ]
              : []),
            ...fields
          ];

          // Render all fields in rows of 4
          const rows = [];
          for (let i = 0; i < allFields.length; i += 4) {
            const rowFields = allFields.slice(i, i + 4);
            rows.push(
              <div key={i} className="row mt-4">
                {rowFields.map((field, index) => (
                  <OrderDetailField
                    key={field.key ?? `${i}-${index}`}
                    field={field}
                    order={estoppel}
                    dateFields={dateFields}
                    priceFields={priceFields}
                    hardCostRequiredStatuses={HARD_COST_REQUIRED_STATUSES}
                    role={role}
                    currentUserInfo={currentUserInfo}
                    loadingMetaData={loadingMetaData}
                    onSyncOrderHardcost={onSyncOrderHardcost}
                    disabled={disabled}
                    onOrderFieldUpdate={onOrderFieldUpdate}
                    allowInlineEditing={!serviceCreation}
                  />
                ))}
              </div>
            );
          }

          return rows;
        })()}
      </div>

      <div className="d-flex align-items-center justify-content-between mt-4">
        <div className="d-flex align-items-center">
          <h6 className="font-weight-semibold xlargeFont mb-0">HOAs:</h6>
          {!isClient(role) && (
            <RequestHoaInfoTrigger requestHoaInfo={estoppel?.requestHoaInfo} />
          )}
        </div>
        {!isClient(role) && (
          <Button
            size="sm"
            variant="dark"
            disabled={disabled}
            className="ml-2 d-inline-flex align-items-center"
            onClick={() => onEditClick(estoppel)}
          >
            <Pencil size={12} className="mr-1" /> Edit HOAs
          </Button>
        )}
      </div>
      <HOAs
        hoas={estoppel?.['homeOwnerInformation']}
        requestHoaInfo={estoppel?.requestHoaInfo}
        service={service}
        serviceCreation={serviceCreation}
        onEditHoaSlot={
          serviceCreation && onEditHoaSlotClick
            ? hoaIndex => onEditHoaSlotClick(estoppel, hoaIndex)
            : undefined
        }
      />

      {isClient(role) &&
        !estoppel?.['cancellationRequested'] &&
        !CLIENT_CANCELLED_INTERNAL_STATUSES.includes(estoppel?.status) && (
        <div className="mt-3 d-flex justify-content-end">
          <Button
            variant="outline-danger"
            className="ml-1 d-inline-flex align-items-center shadow-sm"
            onClick={() => onRequestCancellationClick(estoppel)}
            disabled={disabled}
          >
            <SlashCircle size={13} className="mr-1" />
            Request Order Cancellation
          </Button>
        </div>
      )}
    </div>
  );
};

export default EstoppelDetails;
